import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Footer, Logo } from '../components/Brand';
import { Button, ErrorNote, Field, Notice } from '../components/ui';
import { api } from '../lib/api';

export function SignIn() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [liError, setLiError] = useState(() => new URLSearchParams(location.search).get('li') ?? '');
  const send = useMutation({ mutationFn: () => api('/auth/code', { method: 'POST', json: { email } }), onSuccess: () => setSent(true) });
  const verify = useMutation({ mutationFn: () => api('/auth/code/verify', { method: 'POST', json: { email, code } }), onSuccess: () => nav('/portal') });

  useEffect(() => {
    const take = (r: { ok?: boolean; error?: string; mode?: string }) => {
      if (r.ok && r.mode === 'login') nav('/portal');
      else if (!r.ok) setLiError(r.error ?? 'LinkedIn sign-in did not complete.');
    };
    const onMsg = (e: MessageEvent) => {
      if (e.origin === location.origin && (e.data as { type?: string })?.type === 'mh-linkedin') take((e.data as { result: never }).result);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'mh-linkedin' && e.newValue) take(JSON.parse(e.newValue));
    };
    window.addEventListener('message', onMsg);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('message', onMsg);
      window.removeEventListener('storage', onStorage);
    };
  }, [nav]);

  const linkedIn = () => {
    // Same tab, not a popup: on phones the popup could not hand the sign-in back.
    location.assign('/api/auth/linkedin/start?mode=login');
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-line">
        <div className="mx-auto max-w-6xl px-4 py-3">
          <Logo label="Partner Hub" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-md flex-1 px-4 py-14">
        <p className="eyebrow mb-2">Partners and applicants</p>
        <h1 className="mb-6 text-3xl font-light">Sign in</h1>
        <div className="space-y-5 border border-line bg-card p-6">
          <Button tone="ghost" className="w-full" onClick={linkedIn}>
            Continue with LinkedIn
          </Button>
          <ErrorNote error={liError || null} />
          <div className="flex items-center gap-3 text-sm text-dim">
            <span className="h-px flex-1 bg-line" />
            or with a code sent to your email
            <span className="h-px flex-1 bg-line" />
          </div>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (sent) verify.mutate();
              else send.mutate();
            }}
          >
            <Field label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            {sent && (
              <>
                <Notice>If {email} belongs to a partner or applicant, a 6-digit code is on its way. It expires in 15 minutes.</Notice>
                <Field label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              </>
            )}
            <ErrorNote error={send.error ?? verify.error} />
            <Button type="submit" className="w-full" busy={send.isPending || verify.isPending} disabled={!email || (sent && code.length !== 6)}>
              {sent ? 'Sign in' : 'Email me a code'}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-sm text-ash">
          Not a partner yet? <Link className="text-gold underline" to="/apply">Apply here</Link>.
        </p>
      </main>
      <Footer />
    </div>
  );
}
